// © 2025 Camp Promethean, LLC. All Rights Reserved.

import { createContext, useContext } from "react";

export type FacilityRole = 'owner' | 'admin' | 'manager' | 'member' | 'viewer';

export interface FacilityMember {
  id: string;
  facility_id: string;
  user_id: string;
  role: FacilityRole;
  joined_at: string;
}

export interface Facility {
  id: string;
  name: string;
  slug: string | null;
  invite_code: string | null;
  invite_enabled: boolean;
  owner_id: string | null;
  user_id: string;
  timezone: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface FacilityContextType {
  activeFacility: Facility | null;
  facilities: Facility[];
  userRole: FacilityRole | null;
  members: FacilityMember[];
  loading: boolean;
  switchFacility: (facilityId: string) => void;
  refreshFacilities: () => Promise<void>;
  canWrite: boolean;
  isAdmin: boolean;
}

const FacilityContext = createContext<FacilityContextType | undefined>(undefined);

export function useFacility() {
  const context = useContext(FacilityContext);
  if (context === undefined) {
    throw new Error('useFacility must be used within a FacilityProvider');
  }
  return context;
}

export default FacilityContext;
